import DropdownScrollable from '../../../../DropdownScrollable/DropdownScrollable';
import Ellipses from '../../../../EllipsesControl/EllipsesPresets';
import LoadingIndicator from '../../../../LoadingIndicator/LoadingIndicator';
import {SVGICONS} from '@ey/icons';
import {useEngagementId} from '../../../../../../util/customHooks';
import {clientSideEventNames} from '../../../../../../util/uiconstants';
import {labels} from '../../../../../../util/utils';
import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {v4 as uuidv4} from 'uuid';
import BodySpecialAccordionHeaderColumn from './BodySpecialAccordionHeaderColumn';
import BodySpecialAccordionHeaderOverflow from './BodySpecialAccordionHeaderOverflow';
import ChildEntityList from './RelateItProcessItApp/ChildEntityList';

function ISA315RelateItProcessItAppListItem({
	id,
	byId,
	entityColor,
	entityConfig,
	entityCount,
	entityName,
	entityProps,
	onRemove,
	entityId,
	relateEntityAction,
	header,
	dragAndDrop,
	onClick
}) {
	const entity = useSelector((state) => byId(state, id));
	const [isSaving, setIsSaving] = useState(false);
	const engagementId = useEngagementId();

	const {nameKey, allIds, selector} = entityProps(entity);

	const dispatch = useDispatch();

	const handleOnClick = useCallback((event) => {
		// Avoid toggling selection on the more menu option
		const eventTargetStr = event.target.toString();
		if (!eventTargetStr.includes('SVGElement') && !eventTargetStr.includes('SVGPathElement')) {
			onClick(id), [id, onClick];
		}
	});

	const filteredAllIds = useSelector((state) =>
		nameKey === 'itApplicationName' && Array.isArray(allIds)
			? allIds.filter((id) => Boolean(selector(state, id).isServiceOrganization) === false)
			: allIds
	);

	const value = nameKey === 'itApplicationName' ? filteredAllIds?.length : entityCount(entity);

	const onDragStart = (event) => {
		const element = document.getElementById(uniqueId);
		event.dataTransfer.setData(`canvas-custom/entity/${entity.id}/${entityId}`, JSON.stringify(entity));
		event.dataTransfer.setDragImage(element, 0, 0);
	};

	const onDragOver = (event) => {
		const dataEntityUId = Number(event.dataTransfer.types[0].split('/')[2]);
		const dataEntityId = Number(event.dataTransfer.types[0].split('/')[3]);
		if (!entityProps(entity).allIds.includes(dataEntityUId) && dataEntityId !== entityId) {
			event.preventDefault();
		}
	};

	const onDrop = (event) => {
		const droppedId = JSON.parse(event.dataTransfer.getData(event.dataTransfer.types[0])).id;
		if (!isSaving && !entityProps(entity).allIds.includes(droppedId)) {
			setIsSaving(true);
			dispatch(relateEntityAction(engagementId, entity.id, droppedId)).finally(() => {
				setIsSaving(false);
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			});
		}
	};

	const uniqueId = React.useMemo(() => {
		return `${id}_RelateItProcessItAppListItem_${uuidv4()}`;
	}, []);

	return (
		<StyledISA315RelateItProcessItAppListItem
			className={`StyledISA315RelateItProcessItAppListItem`}
			color={!entity.selected ? 'var(--neutrals-300)' : entityColor}
			highlightColor={entity.selected ? 'var(--neutrals-900)' : 'var(--neutrals-300)'}
			SelectedNonAssociatedBgColor={'var(--neutrals-00white)'}
			SelectedAssociatedBgColor={!entity.hasAssociation ? 'var(--neutrals-00white)' : entityColor}
			onClick={handleOnClick}
			id={uniqueId}
			onDragOver={onDragOver}
			onDrop={onDrop}
		>
			<section
				className={`BorderColorHighlighter
							${entity.selected ? 'SelectedEntity' : ''}
							${entity.hasAssociation ? 'SelectedEntityandAssociated' : ''}`}
			>
				&nbsp;
			</section>
			<section className="parent-container">
				<section className={`container-content ${dragAndDrop ? 'dragAndDropAvailable' : ''}`}>
					<section className="header-section">
						{dragAndDrop && (
							<section className="drag-icon" draggable title={labels.drag} onDragStart={onDragStart}>
								<SVGICONS styleName="medium" name={'drag-indicator'} hoverText={labels.drag} />
							</section>
						)}
						<section
							draggable={dragAndDrop}
							className="isa315relateitprocessitappentitynameitem"
							onDragStart={onDragStart}
						>
							<Ellipses.Single>{entityName(entity)}</Ellipses.Single>
						</section>
						<BodySpecialAccordionHeaderColumn
							className="isa315relateitprocessitapplistitem"
							header={header}
							value={value || '-'}
						/>
						<DropdownScrollable>
							<BodySpecialAccordionHeaderOverflow config={entityConfig(entity)} setIsOpen={() => {}} />
						</DropdownScrollable>
					</section>
					{isSaving ? (
						<LoadingIndicator view={'inlineView'} show={isSaving} />
					) : (
						<ChildEntityList key={`${id}_card_list`} {...entityProps(entity)} onRemove={onRemove} fatherId={id} />
					)}
				</section>
			</section>
		</StyledISA315RelateItProcessItAppListItem>
	);
}

ISA315RelateItProcessItAppListItem.propTypes = {
	engagementId: PropTypes.number.isRequired,
	bodyId: PropTypes.number.isRequired,
	id: PropTypes.number.isRequired,
	byId: PropTypes.func.isRequired,
	entityColor: PropTypes.string,
	entityConfig: PropTypes.func.isRequired,
	entityCount: PropTypes.func.isRequired,
	entityName: PropTypes.func.isRequired,
	entityProps: PropTypes.func.isRequired,
	header: PropTypes.string.isRequired,
	dragAndDrop: PropTypes.bool,
	entityId: PropTypes.number.isRequired,
	onRemove: PropTypes.func.isRequired
};

ISA315RelateItProcessItAppListItem.defaultProps = {
	dragAndDrop: false,
	entityColor: 'var(--green-600)'
};

export default ISA315RelateItProcessItAppListItem;

const StyledISA315RelateItProcessItAppListItem = styled.section`
	margin-top: var(--px-10);
	padding: 0;
	font-size: var(--px-16);
	background-color: var(--neutrals-00white);
	border: var(--px-1) solid var(--neutrals-200);
	border-radius: var(--px-4);
	display: flex;
	&:first-child {
		margin-top: 0;
	}
	.BorderColorHighlighter {
		background-color: var(--neutrals-00white);
		width: var(--px-4);
		border-radius: var(--px-4) 0 0 var(--px-4);
		&.SelectedEntity {
			background-color: ${(props) => props.theme.color[props.SelectedNonAssociatedBgColor]};
			&.SelectedEntityandAssociated {
				background-color: ${(props) => props.theme.color[props.SelectedAssociatedBgColor]};
			}
		}
	}
	.parent-container {
		display: flex;
		padding: var(--px-10) var(--px-10) var(--px-10) var(--px-6);
		width: calc(100% - var(--px-4));
		.container-content {
			display: flex;
			flex-direction: column;
			width: 100%;
			&.dragAndDropAvailable {
				.header-section {
					.isa315relateitprocessitappentitynameitem {
						user-select: none;
						width: calc(100% - var(--px-200));
						height: 100%;
						section {
							height: 100%;
						}
					}
					.isa315relateitprocessitappentitynameitem:hover {
						cursor: pointer;
					}
				}
				.entity-list {
					margin-left: var(--px-10) !important;
				}
			}
			.header-section {
				height: 100%;
				display: flex;
				font-weight: bold;
				align-items: center;
				.drag-icon {
					height: 100%;
					width: var(--px-20);
					display: inline-flex;
					align-items: center;
					svg {
						fill: var(--neutrals-900);
					}
				}
				.isa315relateitprocessitappentitynameitem {
					width: calc(100% - var(--px-180));
				}
				.isa315relateitprocessitapplistitem {
					width: var(--px-150);
					padding: 0 var(--px-20);
				}
				.body-special-accordion-header-overflow {
					width: var(--px-30);
				}
				.DropdownScrollable {
					min-height: unset !important;
				}
			}
		}
	}
`;
